import { signOut } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { AnimatePresence, motion } from "framer-motion";
import {
	BarChart,
	BookOpen,
	HelpCircle,
	LogOut,
	Menu,
	MessageSquare,
	Settings as SettingsIcon,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";
import { Tooltip } from "react-tooltip";
import Auth from "./components/Auth";
import Dashboard from "./components/Dashboard";
import FAQ from "./components/FAQ";
import Journal from "./components/Journal";
import JournalHome from "./components/JournalHome";
import Payment from "./components/Payment";
import Settings from "./components/Settings";
import Therapist from "./components/Therapist";
import TherapyHome from "./components/TherapyHome";
import { useUser } from "./contexts/UserContext";
import { auth, db } from "./firebaseConfig";

const App = () => {
	const { user, setUser, loading } = useUser();
	const navigate = useNavigate();
	const location = useLocation();
	const [hasSubscription, setHasSubscription] = useState(false);
	const [isNavOpen, setIsNavOpen] = useState(false);
	const [freeEntriesLeft, setFreeEntriesLeft] = useState(0);
	const [freeSessionsLeft, setFreeSessionsLeft] = useState(0);

	useEffect(() => {
		const checkSubscription = async () => {
			if (user?.uid) {
				const userRef = doc(db, "Users", user.uid);
				const userDoc = await getDoc(userRef);
				if (userDoc.exists()) {
					const userData = userDoc.data();

					setFreeEntriesLeft(userData.freeEntriesLeft ?? 0);
					setFreeSessionsLeft(userData.freeSessionsLeft ?? 0);

					const now = new Date();
					const isActive = userData.subscriptionActive;
					const endDate = userData.subscriptionEndDate?.toDate();
					const trialEnd = userData.subscriptionTrialEnd?.toDate();
					const subscriptionStatus = userData.subscriptionStatus;

					// Check various conditions that would invalidate access
					const isTrialExpired = trialEnd && now > trialEnd;
					const isSubscriptionExpired = endDate && now > endDate;
					const isTrialEnded = subscriptionStatus === "trial_expired";

					// Allow access if subscription is active and not expired,
					// even if it's canceled (access continues until period end)
					const hasValidAccess =
						isActive &&
						!isTrialExpired &&
						!isSubscriptionExpired &&
						!isTrialEnded;

					setHasSubscription(hasValidAccess);
				} else {
					setHasSubscription(false);
				}
			}
		};

		checkSubscription();

		// Real-time listener for subscription changes
		if (user?.uid) {
			const userRef = doc(db, "Users", user.uid);
			const unsubscribe = onSnapshot(userRef, (doc) => {
				if (doc.exists()) {
					const userData = doc.data();
					const now = new Date();
					const isActive = userData.subscriptionActive;
					const endDate = userData.subscriptionEndDate?.toDate();
					const trialEnd = userData.subscriptionTrialEnd?.toDate();
					const subscriptionStatus = userData.subscriptionStatus;

					const isTrialExpired = trialEnd && now > trialEnd;
					const isSubscriptionExpired = endDate && now > endDate;
					const isTrialEnded = subscriptionStatus === "trial_expired";

					const hasValidAccess =
						isActive &&
						!isTrialExpired &&
						!isSubscriptionExpired &&
						!isTrialEnded;

					setHasSubscription(hasValidAccess);
				} else {
					setHasSubscription(false);
				}
			});

			return () => unsubscribe();
		}
	}, [user, location.pathname, navigate]);

	const handleNavItemClick = (path) => {
		navigate(path);
		setIsNavOpen(false);
	};

	const handleLogout = async () => {
		try {
			await signOut(auth);
			setUser(null);
		} catch (error) {
			console.error("Error signing out:", error);
		}
	};

	const NavBar = () => (
		<>
			{/* Mobile Menu Button */}
			<AnimatePresence>
				{!isNavOpen && (
					<motion.button
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="md:hidden fixed top-2 left-2 z-50 p-2 rounded-md bg-transparent"
						onClick={() => setIsNavOpen(!isNavOpen)}>
						<Menu size={24} />
					</motion.button>
				)}
			</AnimatePresence>

			{/* Desktop Navigation - Always visible on md and up */}
			<div className="hidden md:flex w-24 h-screen bg-gray-100 text-gray-800 p-4 border-r border-gray-200 shadow-md flex-col">
				<div className="w-full">
					<button
						data-tooltip-id="nav-tooltip"
						data-tooltip-content="Logout"
						className="w-full flex items-center p-2 text-red-600 hover:bg-red-100 rounded justify-center h-10"
						onClick={handleLogout}>
						<LogOut size={16} />
					</button>
				</div>
				<nav className="flex-1 flex items-center">
					<ul className="space-y-4 w-full">
						<li
							data-tooltip-id="nav-tooltip"
							data-tooltip-content="Dashboard"
							onClick={() => handleNavItemClick("/dashboard")}
							className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
								location.pathname.startsWith("/dashboard")
									? "bg-gray-300"
									: ""
							}`}>
							<BarChart size={16} />
						</li>
						<li
							data-tooltip-id="nav-tooltip"
							data-tooltip-content="Talk"
							onClick={() => handleNavItemClick("/therapy-home")}
							className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
								location.pathname.startsWith("/therapy")
									? "bg-gray-300"
									: ""
							}`}>
							<MessageSquare size={16} />
						</li>
						<li
							data-tooltip-id="nav-tooltip"
							data-tooltip-content="Write"
							onClick={() => handleNavItemClick("/journal")}
							className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
								location.pathname.startsWith("/journal")
									? "bg-gray-300"
									: ""
							}`}>
							<BookOpen size={16} />
						</li>
						<li
							data-tooltip-id="nav-tooltip"
							data-tooltip-content="FAQ"
							onClick={() => handleNavItemClick("/faq")}
							className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
								location.pathname.startsWith("/faq")
									? "bg-gray-300"
									: ""
							}`}>
							<HelpCircle size={19} />
						</li>
					</ul>
				</nav>
				<li
					data-tooltip-id="nav-tooltip"
					data-tooltip-content="Settings"
					onClick={() => handleNavItemClick("/settings")}
					className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
						location.pathname.startsWith("/settings")
							? "bg-gray-300"
							: ""
					}`}>
					<SettingsIcon size={16} />
				</li>
				<Tooltip id="nav-tooltip" place="right" className="z-[9999]" />
			</div>

			{/* Mobile Navigation */}
			<AnimatePresence>
				{isNavOpen && (
					<motion.div
						initial={{ x: "-100%" }}
						animate={{ x: 0 }}
						exit={{ x: "-100%" }}
						transition={{
							type: "spring",
							bounce: 0.1,
							duration: 0.3,
						}}
						className="fixed md:hidden w-64 h-screen bg-gray-100 text-gray-800 p-4 border-r border-gray-200 shadow-md flex flex-col z-40">
						<nav className="flex-1 flex items-center">
							<ul className="space-y-4 w-full">
								<li
									onClick={() =>
										handleNavItemClick("/dashboard")
									}
									className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 ${
										location.pathname.startsWith(
											"/dashboard"
										)
											? "bg-gray-300"
											: ""
									}`}>
									<BarChart size={16} />
									Dashboard
								</li>
								<li
									onClick={() =>
										handleNavItemClick("/therapy-home")
									}
									className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 ${
										location.pathname.startsWith("/therapy")
											? "bg-gray-300"
											: ""
									}`}>
									<MessageSquare size={16} />
									Talk
								</li>
								<li
									onClick={() =>
										handleNavItemClick("/journal")
									}
									className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 ${
										location.pathname.startsWith("/journal")
											? "bg-gray-300"
											: ""
									}`}>
									<BookOpen size={16} />
									Write
								</li>
								<li
									onClick={() => handleNavItemClick("/faq")}
									className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 ${
										location.pathname.startsWith("/faq")
											? "bg-gray-300"
											: ""
									}`}>
									<HelpCircle size={19} />
									FAQ
								</li>
								<li
									onClick={() =>
										handleNavItemClick("/settings")
									}
									className="p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200">
									<SettingsIcon size={16} />
									Settings
								</li>
								<li
									className="p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 text-red-600 hover:bg-red-200"
									onClick={handleLogout}>
									<LogOut size={16} />
									Logout
								</li>
							</ul>
						</nav>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Overlay */}
			<AnimatePresence>
				{isNavOpen && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 0.5 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						className="fixed inset-0 bg-black md:hidden z-30"
						onClick={() => setIsNavOpen(false)}
					/>
				)}
			</AnimatePresence>
		</>
	);

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
			</div>
		);
	}

	// Show auth if no user
	if (!user) {
		return <Auth />;
	}

	// Show payment if no subscription
	if (!hasSubscription) {
		return (
			<div className="flex">
				<NavBar />
				<div className="flex-1">
					<Routes>
						<Route
							path="/therapy"
							element={
								freeSessionsLeft > 0 ? (
									<Therapist isFreeAccess={true} />
								) : (
									<Payment type="Therapy" />
								)
							}
						/>
						<Route path="/journal/:id" element={<Journal />} />
						<Route
							path="/journal"
							element={
								freeEntriesLeft > 0 ? (
									<JournalHome isFreeAccess={true} />
								) : (
									<Payment type="Journal" />
								)
							}
						/>
						<Route
							path="/settings"
							element={<Settings isFreeAccess={true} />}
						/>
						<Route path="/dashboard" element={<Dashboard />} />
						<Route path="/faq" element={<FAQ />} />
						<Route path="/therapy-home" element={<TherapyHome />} />
						<Route
							path="*"
							element={<Navigate to="/dashboard" />}
						/>
					</Routes>
				</div>
			</div>
		);
	}

	// Show main app if authenticated and has subscription
	return (
		<div className="flex">
			<NavBar />
			<div className="flex-1">
				<Routes>
					<Route path="/therapy" element={<Therapist />} />
					<Route path="/journal/:id" element={<Journal />} />
					<Route path="/journal" element={<JournalHome />} />
					<Route path="/settings" element={<Settings />} />
					<Route path="/dashboard" element={<Dashboard />} />
					<Route path="/faq" element={<FAQ />} />
					<Route path="/therapy-home" element={<TherapyHome />} />
					<Route path="*" element={<Navigate to="/dashboard" />} />
				</Routes>
			</div>
		</div>
	);
};

export default App;
